/**
 * Blanks view component.
 * @module components/View/BlanksView
 */

import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Input,
  Label,
  Table,
  TableHeader,
  TableRow,
  TableBody,
  TableCell,
  TableHeaderCell,
} from 'semantic-ui-react';
import { Link, useHistory } from 'react-router-dom';

import { Icon } from '@plone/volto/components';
import { flattenToAppURL } from '@plone/volto/helpers';

import downSVG from '@plone/volto/icons/down-key.svg';
import upSVG from '@plone/volto/icons/up-key.svg';
import clearSVG from '@plone/volto/icons/clear.svg';
import addSVG from '@plone/volto/icons/add.svg';

/**
 * Blanks view component class.
 * @function BlanksView
 * @param {Object} content Content object.
 * @returns {string} Markup of the component.
 */
const BlanksView = ({ content }) => {
  const history = useHistory();
  let items = [...content.items];

  // Filtering
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [base, setBase] = useState('');
  const [head, setHead] = useState('');

  const codeRef = useRef(null);
  const nameRef = useRef(null);
  const baseRef = useRef(null);
  const headRef = useRef(null);

  const filterField = (items, field, value) =>
    value === ''
      ? items
      : (items = items.filter(
          (item) =>
            item[field] &&
            item[field].toLowerCase().indexOf(value.toLowerCase()) !== -1,
        ));

  items = filterField(items, 'code', code);
  items = filterField(items, 'name', name);
  items = filterField(items, 'base', base);
  items = filterField(items, 'head', head);

  // Sorting
  const [sort, setSort] = useState('code');
  const [dir, setDir] = useState('asc');

  items = items.sort((a, b) =>
    (dir === 'asc' ? a[sort] > b[sort] : a[sort] < b[sort]) ? 1 : -1,
  );

  const onSort = (value) => {
    if (sort === value) {
      setDir(dir === 'asc' ? 'desc' : 'asc');
    } else {
      setSort(value);
      setDir('asc');
    }
  };

  const SortIcon = () => (
    <Icon
      name={dir === 'desc' ? upSVG : downSVG}
      className="icon"
      size="20px"
    />
  );

  return (
    <article id="blanks">
      <div className="button-toolbar">
        <Link
          className="circular-button primary float-left"
          to={`${flattenToAppURL(content['@id'])}/add?type=Blank`}
        >
          <Icon name={addSVG} size="24px" />
        </Link>
        <div className="button-toolbar-title">
          Blanks <Label circular>{items.length}</Label>
        </div>
      </div>
      <div className="view-padding">
        <div className="scrollable-table">
          <Table striped celled selectable>
            <TableHeader>
              <TableRow className="tools-header">
                <TableHeaderCell onClick={() => onSort('code')} width="2">
                  Code {sort === 'code' && <SortIcon />}
                </TableHeaderCell>
                <TableHeaderCell onClick={() => onSort('name')} width="3">
                  Name {sort === 'name' && <SortIcon />}
                </TableHeaderCell>
                <TableHeaderCell onClick={() => onSort('base')} width="3">
                  Base {sort === 'base' && <SortIcon />}
                </TableHeaderCell>
                <TableHeaderCell onClick={() => onSort('head')} width="4">
                  Head {sort === 'head' && <SortIcon />}
                </TableHeaderCell>
              </TableRow>
              <TableRow className="tools-filter">
                <TableHeaderCell>
                  <div className="filter-input">
                    <Input
                      value={code}
                      placeholder="Filter by code"
                      ref={codeRef}
                      onChange={(e) => setCode(e.target.value.toUpperCase())}
                    />
                    {code && (
                      <Icon
                        name={clearSVG}
                        size="16px"
                        className="clear-icon"
                        onClick={() => {
                          setCode('');
                          codeRef.current?.focus();
                        }}
                      />
                    )}
                  </div>
                </TableHeaderCell>
                <TableHeaderCell>
                  <div className="filter-input">
                    <Input
                      value={name}
                      placeholder="Filter by name"
                      ref={nameRef}
                      onChange={(e) => setName(e.target.value)}
                    />
                    {name && (
                      <Icon
                        name={clearSVG}
                        size="16px"
                        className="clear-icon"
                        onClick={() => {
                          setName('');
                          nameRef.current?.focus();
                        }}
                      />
                    )}
                  </div>
                </TableHeaderCell>
                <TableHeaderCell>
                  <div className="filter-input">
                    <Input
                      value={base}
                      placeholder="Filter by base"
                      ref={baseRef}
                      onChange={(e) => setBase(e.target.value)}
                    />
                    {base && (
                      <Icon
                        name={clearSVG}
                        size="16px"
                        className="clear-icon"
                        onClick={() => {
                          setBase('');
                          baseRef.current?.focus();
                        }}
                      />
                    )}
                  </div>
                </TableHeaderCell>
                <TableHeaderCell>
                  <div className="filter-input">
                    <Input
                      value={head}
                      placeholder="Filter by head"
                      ref={headRef}
                      onChange={(e) => setHead(e.target.value)}
                    />
                    {head && (
                      <Icon
                        name={clearSVG}
                        size="16px"
                        className="clear-icon"
                        onClick={() => {
                          setHead('');
                          headRef.current?.focus();
                        }}
                      />
                    )}
                  </div>
                </TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow
                  className="clickable-row"
                  onClick={() => history.push(flattenToAppURL(item['@id']))}
                >
                  <TableCell>{item.code}</TableCell>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>{item.base}</TableCell>
                  <TableCell>{item.head}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </article>
  );
};

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
BlanksView.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.string,
  }).isRequired,
};

export default BlanksView;
